<template>
  <component
    :is="tag"
    ref="rootEl"
    :to="to || undefined"
    :href="href || undefined"
    :class="[
      'kpi-card group relative rounded-theme-md border transition overflow-hidden',
      'border-theme-border bg-theme-surface',
      isInteractive
        ? 'cursor-pointer hover:border-theme-brand/45 hover:shadow-theme-card focus:outline-none focus:shadow-theme-focus'
        : '',
      density === 'compact' ? 'p-3' : 'p-4',
      hasSideSparkline ? 'flex items-stretch gap-3' : 'flex flex-col',
      hasSideSparkline ? '' : (density === 'compact' ? 'gap-1' : 'gap-1.5'),
    ]"
  >
    <!-- Left column wrapper when sparkline sits on the side. Identical
         vertical rhythm to the stacked layout, just bounded to half the card. -->
    <div
      :class="[
        hasSideSparkline ? 'flex flex-col flex-1 min-w-0' : 'contents',
        hasSideSparkline ? (density === 'compact' ? 'gap-1' : 'gap-1.5') : '',
      ]"
    >
    <!-- Hover actions (edit / pin / remove) -->
    <div
      v-if="hasActions"
      class="absolute top-2 right-2 hidden gap-1 group-hover:flex z-10"
      @click.stop
    >
      <AppButton
        v-if="onEdit"
        type="button"
        variant="ghost"
        tone="muted"
        size="xs"
        icon-only
        :icon="mdiPencilOutline"
        :aria-label="t('ui.kpi_tile_edit')"
        @click.stop="$emit('edit')"
      />
      <AppButton
        v-if="onTogglePin"
        type="button"
        variant="ghost"
        :tone="pinned ? 'primary' : 'muted'"
        size="xs"
        icon-only
        :icon="pinned ? mdiPin : mdiPinOutline"
        :aria-label="pinned ? t('ui.kpi_tile_unpin') : t('ui.kpi_tile_pin')"
        @click.stop="$emit('toggle-pin', !pinned)"
      />
      <AppButton
        v-if="onRemove"
        type="button"
        variant="ghost"
        tone="danger"
        size="xs"
        icon-only
        :icon="mdiClose"
        :aria-label="t('ui.kpi_tile_remove')"
        @click.stop="$emit('remove')"
      />
    </div>

    <!-- Title. Auto-shrinks via useAutoshrinkText so even long labels like
         "aFRR capaciteitsvergoeding opregelen" fit on one line at narrow
         card widths. We deliberately drop `tracking-wide` here — at narrow
         card widths it's purely padding that fights the auto-shrink. -->
    <h3
      ref="titleEl"
      class="kpi-card__label min-w-0 overflow-visible whitespace-nowrap font-semibold uppercase text-theme-text-secondary"
    >
      {{ displayLabel }}
    </h3>

    <template v-if="!hasError">
      <!-- Value + unit. Same one-line guarantee — measure the row, shrink the
           value's font-size if the value+unit pair would otherwise overflow. -->
      <div
        ref="valueRowEl"
        class="flex items-baseline gap-1.5 min-w-0 flex-nowrap overflow-hidden"
      >
        <span
          ref="valueEl"
          class="kpi-card__value whitespace-nowrap font-bold leading-none tracking-tight tabular-nums text-theme-text"
        >
          {{ formattedValue }}
        </span>
        <span
          v-if="displayUnit"
          class="shrink-0 whitespace-nowrap text-xs text-theme-text-muted"
        >
          {{ displayUnit }}
        </span>
      </div>

      <!-- Represented period / context line -->
      <p
        v-if="contextLine"
        ref="contextEl"
        class="kpi-card__context min-w-0 overflow-visible whitespace-nowrap text-theme-text-muted"
      >
        {{ contextLine }}
      </p>

      <!-- PI-432: the tile already named its period; freshness is the other half
           of "can I trust this number". -->
      <p
        v-if="freshnessLine"
        class="kpi-card__freshness min-w-0 overflow-visible whitespace-nowrap text-theme-text-muted"
        data-testid="kpi-card-freshness"
      >
        {{ freshnessLine }}
      </p>

      <!-- Reference / delta line. The pill is fixed-size; the reference
           label after it auto-shrinks within whatever space remains. -->
      <div
        v-if="hasReference"
        ref="referenceRowEl"
        class="flex items-center gap-2 min-w-0 mt-0.5 overflow-hidden"
      >
        <span
          :class="[
            'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums leading-none shrink-0',
            deltaPillClass,
          ]"
        >
          <icon :path="deltaIcon" size="10" />
          <span>{{ deltaText }}</span>
        </span>
        <span
          v-if="referenceLine"
          ref="referenceLabelEl"
          class="min-w-0 whitespace-nowrap text-theme-text-muted"
        >
          {{ referenceLine }}
        </span>
      </div>
    </template>

    <template v-else>
      <div
        ref="valueRowEl"
        class="flex items-baseline gap-1.5 min-w-0 flex-nowrap overflow-hidden"
        :aria-label="errorLabel"
      >
        <span
          ref="valueEl"
          class="kpi-card__value whitespace-nowrap font-bold leading-none tabular-nums text-theme-text-muted"
        >
          -
        </span>
      </div>
      <p
        ref="contextEl"
        class="kpi-card__context min-w-0 overflow-visible whitespace-nowrap text-theme-text-muted"
      >
        {{ errorLabel }}
      </p>
    </template>
    </div>
    <!-- /left column wrapper -->

    <!-- Subtle sparkline. Two positions:
         • Side (wide cards) — vertically centered, flexes to fill the right
           half. Mirrors the CoinMarketCap-style ticker card layout.
         • Bottom (narrow cards) — bleeds into the card's edges so the line
           kisses the rounded corners. -->
    <div
      v-if="sparklineData.length && hasSideSparkline"
      class="kpi-card__sparkline self-stretch flex items-center min-w-0 flex-1"
    >
      <sparkline-chart
        :data="sparklineData"
        :color="sparklineColor"
        :height="sideSparklineHeight"
        :show-area="false"
        :step="resolvedSparklineStep"
      />
    </div>
    <div
      v-else-if="sparklineData.length"
      :class="['kpi-card__sparkline mt-auto', density === 'compact' ? '-mx-3 -mb-3' : '-mx-4 -mb-4']"
    >
      <sparkline-chart
        :data="sparklineData"
        :color="sparklineColor"
        :height="sparklineHeight"
        :show-area="false"
        :step="resolvedSparklineStep"
        flush
      />
    </div>
  </component>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import SparklineChart from '@/components/ui/data-display/SparklineChart.vue';
  import { useAutoshrinkText } from '@/composables/useAutoshrinkText';
  import { useKpiReferenceLabel } from '@/composables/useKpiReferenceLabel';
  import { resolveKpiFreshnessLabel } from '@/components/visualizations/kpi/kpiProvenance';
  import { getTrendDirectionColor } from './trendDirectionPalette';
  import {
    mdiArrowBottomRight,
    mdiArrowRight,
    mdiArrowTopRight,
    mdiClose,
    mdiPencilOutline,
    mdiPin,
    mdiPinOutline,
  } from '@mdi/js';
  import { computed, onBeforeUnmount, onMounted, ref, useAttrs } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({
    /**
     * The full KpiValueResponse from the backend (preferred). Carries value,
     * unit, change_pct, value_mode, reference_mode, context_label, sparkline,
     * value_status — everything needed to render. When passed, the flat
     * label/value/unit/delta props are used only as fallbacks.
     */
    kpiValue: { type: Object, default: null },
    /** Fallback label when kpiValue.label is missing. */
    label: { type: String, default: '' },
    /** Fallback value (used only when kpiValue is null). */
    value: { type: [Number, String, null], default: null },
    /** Fallback unit. */
    unit: { type: String, default: '' },
    /** Override the resolved context line. Useful for adding a dataset prefix. */
    contextLineOverride: { type: String, default: '' },
    /** Compact mode (smaller padding + value, used in MyKpis grids). */
    density: { type: String, default: 'standard' },
    /** Sparkline height in px (used when the sparkline sits at the bottom). */
    sparklineHeight: { type: Number, default: 28 },
    /** Sparkline height when it sits on the side of the card. */
    sideSparklineHeight: { type: Number, default: 56 },
    /** Force-hide the sparkline regardless of payload. */
    hideSparkline: { type: Boolean, default: false },
    /**
     * Step-line override. By default the card reads `kpi_value.sparkline_step`
     * from the API (set on KPIs whose underlying series is piecewise-constant
     * — DA prices, block capacity prices, imbalance settlements). Pass this
     * prop to force it on/off without backend cooperation.
     *   null / undefined — defer to kpi_value.sparkline_step
     *   false            — never step, even if the backend says so
     *   true / string    — step-left (or explicit 'left'/'center'/'right')
     */
    sparklineStep: { type: [String, Boolean], default: null },
    /**
     * Layout for the sparkline relative to the value column.
     *   'auto'   — side when the card is wide enough, otherwise bottom
     *   'side'   — always render to the right (CoinMarketCap-style)
     *   'bottom' — always render flush to the bottom edge
     */
    sparklinePosition: {
      type: String,
      default: 'auto',
      validator: (value) => ['auto', 'side', 'bottom'].includes(value),
    },
    /**
     * Card width (in px) above which the auto-layout flips to a side
     * sparkline. 320px gives the value column ~150–170px which is enough
     * for "1.234,56 €/MWh" + a delta pill without the value wrapping.
     */
    sideLayoutMinWidth: { type: Number, default: 320 },
    /** Pinned state for the pin/unpin toggle. */
    pinned: { type: Boolean, default: false },
    /** Soft error state rendered in place of value+context+reference. */
    error: { type: [String, Boolean], default: null },
    /** When the source is stale we render a muted ribbon below the title. */
    staleSince: { type: [String, Date, null], default: null },
    /** Router-link target. When set the card becomes interactive. */
    to: { type: [String, Object], default: null },
    /** Href target as an alternative to `to`. */
    href: { type: String, default: '' },
  });

  defineEmits(['edit', 'toggle-pin', 'remove']);

  const { t, locale } = useI18n();
  const attrs = useAttrs();
  const { referenceLabel } = useKpiReferenceLabel();

  const onEdit = computed(() => Boolean(attrs.onEdit));
  const onTogglePin = computed(() => Boolean(attrs.onTogglePin));
  const onRemove = computed(() => Boolean(attrs.onRemove));
  const hasActions = computed(() => onEdit.value || onTogglePin.value || onRemove.value);

  // ── Width-aware layout ─────────────────────────────────────────────────
  // We watch the card's own width (not the viewport) so the layout adapts
  // correctly when the card is dropped into different grid columns — the
  // home Pulse strip is 4-up, MyKpis is 3-up, the dashboard builder is
  // free-form, etc. A ResizeObserver is cheaper than a media query here
  // because the relevant dimension is the card itself, not the viewport.
  const rootEl = ref(null);
  const cardWidth = ref(0);
  let resizeObserver = null;

  onMounted(() => {
    const target = rootEl.value?.$el ?? rootEl.value;
    if (!target || typeof ResizeObserver === 'undefined') return;
    resizeObserver = new ResizeObserver(([entry]) => {
      cardWidth.value = entry.contentRect.width;
    });
    resizeObserver.observe(target);
  });

  onBeforeUnmount(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  const hasSideSparkline = computed(() => {
    if (props.hideSparkline) return false;
    if (props.sparklinePosition === 'side') return true;
    if (props.sparklinePosition === 'bottom') return false;
    return cardWidth.value >= props.sideLayoutMinWidth;
  });

  // ── Single-line auto-shrink for every text element ─────────────────────
  // Truncation hides information; for a value-bearing surface we shrink
  // instead. Each text element gets its own observer + min/max bounds so
  // they scale independently — a long label can shrink while the value
  // stays large, and vice versa.
  const titleEl = ref(null);
  const valueEl = ref(null);
  const valueRowEl = ref(null);
  const contextEl = ref(null);
  const referenceRowEl = ref(null);
  const referenceLabelEl = ref(null);

  // Tailwind sizes the card uses by default — these are also the autoshrink
  // ceilings, so a wide card looks exactly as designed and only narrow
  // cards see the text scaled down.
  const valueMaxPx = computed(() => (props.density === 'compact' ? 20 : 30));
  const valueMinPx = computed(() => (props.density === 'compact' ? 13 : 15));

  // NB: the useAutoshrinkText() calls live further down, after the computed
  // values they watch (displayLabel, formattedValue, displayUnit, contextLine,
  // referenceLine, deltaText) are declared. Vue's `watch` evaluates its source
  // getters eagerly during setup, so invoking them here would read those
  // `const`s inside their temporal dead zone and throw "Cannot access … before
  // initialization".

  const isInteractive = computed(() => Boolean(props.to || props.href));
  const tag = computed(() => {
    if (props.to) return 'router-link';
    if (props.href) return 'a';
    return 'div';
  });

  const displayLabel = computed(() => props.kpiValue?.label || props.label || '');

  function formatNumber(v) {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'number')
      return v.toLocaleString(locale.value, { maximumFractionDigits: 2 });
    return String(v);
  }

  const numericValue = computed(() => {
    const v = props.kpiValue?.value ?? props.value;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  });

  const formattedValue = computed(() => formatNumber(numericValue.value ?? (props.kpiValue?.value ?? props.value)));
  const displayUnit = computed(() => props.kpiValue?.unit || props.unit || '');

  // ── Context line (represented period) ──────────────────────────────────
  // Prefer the server-resolved context_label — it already encodes value_mode
  // ("Current ISP · 09:00–09:15", "Latest fixing · 27 May", etc.). When the
  // caller wants to prepend a dataset prefix they can pass `contextLineOverride`.
  const contextLine = computed(() => {
    if (props.contextLineOverride) return props.contextLineOverride;
    return props.kpiValue?.context_label || '';
  });

  // ── Freshness (PI-432) ─────────────────────────────────────────────────
  // The period says which window the number covers; this says how recently it
  // was computed. Same standard dd-mm-yyyy notation as everywhere else.
  const freshnessLine = computed(() => {
    const stamp = resolveKpiFreshnessLabel([{ last_updated: props.kpiValue?.timestamp }]);
    return stamp ? t('dataset.kpi_card.updated_at', { date: stamp }) : '';
  });

  // ── Reference / delta ───────────────────────────────────────────────────
  const changePct = computed(() => {
    const pct = props.kpiValue?.change_pct;
    return typeof pct === 'number' && Number.isFinite(pct) ? pct : null;
  });

  const hasReference = computed(() => changePct.value !== null);

  const deltaDirection = computed(() => {
    const pct = changePct.value;
    if (pct === null || Math.abs(pct) < 0.1) return 'flat';
    return pct > 0 ? 'up' : 'down';
  });

  const deltaIcon = computed(() => {
    const dir = deltaDirection.value;
    if (dir === 'up') return mdiArrowTopRight;
    if (dir === 'down') return mdiArrowBottomRight;
    return mdiArrowRight;
  });

  const deltaPillClass = computed(() => {
    const dir = deltaDirection.value;
    if (dir === 'up')
      return 'bg-theme-success/10 text-theme-success';
    if (dir === 'down')
      return 'bg-theme-danger/10 text-theme-danger';
    return 'bg-theme-bg-subtle text-theme-text-secondary';
  });

  const deltaText = computed(() => {
    const pct = changePct.value;
    if (pct === null) return '';
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct.toLocaleString(locale.value, { maximumFractionDigits: 1 })}%`;
  });

  const referenceLine = computed(() => referenceLabel(props.kpiValue));

  // ── Single-line auto-shrink for every text element ─────────────────────
  // Declared here (not next to the element refs above) because each watcher
  // source reads a computed defined above; `watch` runs its getter eagerly,
  // so these must come after those declarations to avoid a TDZ error.
  useAutoshrinkText({
    el: titleEl,
    // 7px is past the polite floor but the contract is "single line at any
    // width" — at typical KPI label lengths this never kicks in; only a
    // 35+ character library label on a 200px card hits it.
    min: 7,
    max: 12,
    watchSources: [() => displayLabel.value, cardWidth, hasSideSparkline],
  });
  useAutoshrinkText({
    el: valueEl,
    container: valueRowEl,
    min: valueMinPx,
    max: valueMaxPx,
    step: 1,
    watchSources: [() => formattedValue.value, () => displayUnit.value, cardWidth, hasSideSparkline],
  });
  useAutoshrinkText({
    el: contextEl,
    min: 8,
    max: 11,
    watchSources: [() => contextLine.value, cardWidth, hasSideSparkline],
  });
  useAutoshrinkText({
    el: referenceLabelEl,
    container: referenceRowEl,
    min: 8,
    max: 10,
    watchSources: [() => referenceLine.value, () => deltaText.value, cardWidth, hasSideSparkline],
  });

  // ── Sparkline ────────────────────────────────────────────────────────────
  // Subtle thin line, no fill. Color is mute slate when flat, otherwise
  // matches the delta direction.
  const sparklineColor = computed(() =>
    getTrendDirectionColor(deltaDirection.value, { neutralVariant: 'light' })
  );

  const sparklineData = computed(() => {
    if (props.hideSparkline) return [];
    const pts = props.kpiValue?.sparkline;
    if (!pts || !pts.length) return [];
    return pts.map((p) => ({ t: p.t, v: p.v }));
  });

  // Resolve step-line: explicit prop wins; otherwise read from the API
  // payload. `false` from the prop overrides the API (intentional disable);
  // `null`/`undefined` means "no opinion, defer to backend".
  const resolvedSparklineStep = computed(() => {
    if (props.sparklineStep === false) return false;
    if (props.sparklineStep !== null && props.sparklineStep !== undefined) {
      return props.sparklineStep;
    }
    return props.kpiValue?.sparkline_step || false;
  });

  // ── Error / fallback ────────────────────────────────────────────────────
  const hasError = computed(() => Boolean(props.error));
  const errorLabel = computed(() => t('ui.pulse_tile_error'));
</script>
